import './tenants.scss'

import { Button, Result, Table } from 'antd'
import classnames from 'classnames'
import React, { useMemo, useState } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetcher } from '../../../api'
import { buildTableFilterKeys } from '../../../components/tables/build-table-filter-keys'
import { CreateTenantDrawer } from '../CreateTenant'
import { DeleteTenantModal } from '../DeleteTenant'
import { messages } from '../messages'
import { Tenant } from '../models'
import { TenantDetailsModal } from '../Tenant'
import { UpdateTenantRatesheetDrawer } from '../UpdateRatesheet'
import { UpdateTenantDrawer } from '../UpdateTenant'
import { UpdateTenantLicensesDrawer } from '../UpdateTenantLicenses'
import { buildTenantsTableColumns } from './tenants-table-columns'
import { TenantsSummary } from './TenantsSummary'

export const Tenants = () => {
  const [showAddTenant, setShowAddTenant] = useState(false)
  const [showUpdate, setShowUpdate] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [showUpdateLicenses, setShowUpdateLicenses] = useState(false)

  const [tenantId, setTenantId] = useState(null)
  const [tenantToDelete, setTenantToDelete] = useState(null)
  const [tenantToUpdateRatesheet, setTenantToUpdateRatesheet] = useState(null)

  const { data: tenants, mutate, error } = useSWR<Tenant[]>('/tenants', fetcher, { suspense: true })

  const tableFilterMenuItems = useMemo(
    () => ({
      segmentCodes: buildTableFilterKeys(tenants, 'segmentCode'),
    }),
    [tenants],
  )

  const onUpdate = (currentTenantId: number) => {
    setTenantId(currentTenantId)
    setShowUpdate(true)
  }

  const onDetail = (currentTenantId: number) => {
    setTenantId(currentTenantId)
    setShowDetails(true)
  }

  const onDelete = (tenant: Tenant) => {
    setTenantId(tenant.id)
    setTenantToDelete(tenant)
  }

  const onUpdateRatesheet = (tenant: Tenant) => {
    setTenantId(tenant.id)
    setTenantToUpdateRatesheet(tenant)
  }

  const onUpdateLicenses = (currentTenantId: number) => {
    setTenantId(currentTenantId)
    setShowUpdateLicenses(true)
  }

  const onClose = () => {
    setShowAddTenant(false)
    setShowUpdate(false)
    setShowDetails(false)
    setShowUpdateLicenses(false)
    setTenantId(null)
    setTenantToDelete(null)
    setTenantToUpdateRatesheet(null)
  }

  const onSuccess = () => {
    mutate()
    onClose()
  }

  const columns = buildTenantsTableColumns({
    onUpdate,
    onDetail,
    onDelete,
    onUpdateRatesheet,
    onUpdateLicenses,
    tableFilterMenuItems,
  })

  if (error) {
    return <Result status="error" title={messages.tenant.list.error} />
  }

  return (
    <Column className="tenants">
      <TenantsSummary tenants={tenants} />

      <Row justifyContentEnd>
        <Button type="primary" onClick={() => setShowAddTenant(true)}>
          Create New Tenant
        </Button>
      </Row>

      <Table
        rowClassName={(tenant: Tenant) => classnames({ active: tenant.id === tenantId })}
        columns={columns}
        dataSource={tenants}
        loading={!tenants}
        rowKey="id"
        pagination={false}
        size="small"
      />

      <CreateTenantDrawer isVisible={showAddTenant} onClose={onClose} onSuccess={onSuccess} />
      <UpdateTenantDrawer tenantId={tenantId} isVisible={showUpdate} onClose={onClose} onSuccess={onSuccess} />
      <TenantDetailsModal tenantId={tenantId} isVisible={showDetails} onClose={onClose} />
      <DeleteTenantModal tenant={tenantToDelete} isVisible={!!tenantToDelete} onClose={onClose} onSuccess={onSuccess} />
      <UpdateTenantLicensesDrawer
        tenantId={tenantId}
        isVisible={showUpdateLicenses}
        onClose={onClose}
        onSuccess={onSuccess}
      />
      <UpdateTenantRatesheetDrawer
        tenantDomain={tenantToUpdateRatesheet?.domain}
        tenantName={tenantToUpdateRatesheet?.name}
        isVisible={tenantToUpdateRatesheet}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </Column>
  )
}
